import { useState } from "react";
import { toast } from "sonner";
import { Loader2, PackagePlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UnapprovedTypesDialog } from "@/components/panel/UnapprovedTypesDialog";

export const ORDER_TYPES = [
  { key: "glass", label: "Glass Order" },
  { key: "extrusions", label: "Extrusions Order" },
  { key: "gaskets", label: "Gaskets Order" },
  { key: "fasteners", label: "Fasteners Order" },
];

export const COMPLETE_PANEL = { key: "complete_panel", label: "Complete Panel Order" };

export const ORDER_TYPE_LABELS = Object.fromEntries(
  [...ORDER_TYPES, COMPLETE_PANEL].map((type) => [type.key, type.label])
);

/** Procurement order picker. Complete Panel Order is exclusive of every other type. */
export function CreateOrderPanel({ projectId, selection, onCreated }) {
  const [code, setCode] = useState("");
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [unapproved, setUnapproved] = useState(null);
  const complete = types.includes(COMPLETE_PANEL.key);

  const toggle = (key) => {
    if (key === COMPLETE_PANEL.key) {
      setTypes(complete ? [] : [COMPLETE_PANEL.key]);
      return;
    }
    setTypes((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const submit = async () => {
    const trimmed = code.trim();
    if (busy || !trimmed || types.length === 0 || selection.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/orders`, {
        code: trimmed,
        order_types: types,
        panel_uids: selection,
      });
      toast.success(`Order ${data.code} created for ${data.panel_count} panel(s)`, {
        description: types.map((key) => ORDER_TYPE_LABELS[key]).join(", "),
      });
      setCode("");
      setTypes([]);
      onCreated?.();
    } catch (error) {
      const list = error?.response?.data?.detail?.unapproved;
      if (list?.length) setUnapproved(list);
      else toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="create-order-panel" className="space-y-4 px-4 py-4">
      <div>
        <p className="mono mb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Order code *
        </p>
        <input
          type="text"
          data-testid="order-code-input"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={busy}
          maxLength={80}
          placeholder="e.g. GLS-2026-001"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 disabled:bg-slate-50"
        />
        {!code.trim() ? (
          <p data-testid="order-code-hint" className="mt-1 text-[11px] text-slate-400">
            Required — the order cannot be created without a code.
          </p>
        ) : null}
      </div>

      <div>
        <p className="mono mb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Order types
        </p>
        <div className="space-y-1.5">
          {ORDER_TYPES.map((type) => (
            <label
              key={type.key}
              className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 text-[12px] transition-colors ${
                complete
                  ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                  : types.includes(type.key)
                    ? "cursor-pointer border-cyan-300 bg-cyan-50/70 text-slate-800"
                    : "cursor-pointer border-slate-200 bg-white text-slate-700 hover:border-cyan-300"
              }`}
            >
              <input
                type="checkbox"
                data-testid={`order-type-${type.key}`}
                checked={types.includes(type.key)}
                disabled={complete || busy}
                onChange={() => toggle(type.key)}
                className="h-3.5 w-3.5 accent-cyan-600"
              />
              {type.label}
            </label>
          ))}
        </div>
      </div>

      <label
        className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-2.5 py-2 text-[12px] font-medium transition-colors ${
          complete
            ? "border-cyan-400 bg-cyan-50 text-cyan-800"
            : "border-slate-300 bg-white text-slate-700 hover:border-cyan-300"
        }`}
      >
        <input
          type="checkbox"
          data-testid={`order-type-${COMPLETE_PANEL.key}`}
          checked={complete}
          disabled={busy}
          onChange={() => toggle(COMPLETE_PANEL.key)}
          className="h-3.5 w-3.5 accent-cyan-600"
        />
        {COMPLETE_PANEL.label}
      </label>
      {complete ? (
        <p data-testid="complete-panel-hint" className="text-[11px] text-slate-500">
          Complete Panel Order covers every material, so the individual order types are disabled.
        </p>
      ) : null}

      <Button
        data-testid="create-order-button"
        onClick={submit}
        disabled={!code.trim() || types.length === 0 || busy}
        className="h-9 w-full rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500 disabled:opacity-40"
      >
        {busy ? (
          <Loader2 size={13} className="mr-1.5 animate-spin" />
        ) : (
          <PackagePlus size={13} className="mr-1.5" />
        )}
        {busy ? "Saving order…" : `Create order for ${selection.length} panels`}
      </Button>

      <UnapprovedTypesDialog
        open={Boolean(unapproved)}
        onOpenChange={(next) => !next && setUnapproved(null)}
        types={unapproved}
      />
    </div>
  );
}
